import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RiskService } from "../../services/riskService";
import { MetadataService } from "../../services/metadataService";

const CreateRiskForm = ({
  open,
  onClose,
  organizationId,
  businessUnitId,
  onRiskCreated,
  isOrgAdmin = false,
  forceOrganizationLevel = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    severity_id: "",
    likelihood_id: "",
  });
  const [isOrganizationLevel, setIsOrganizationLevel] = useState(
    forceOrganizationLevel
  );
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [likelihoods, setLikelihoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchMetadata = async () => {
    try {
      setLoading(true);

      const [categoriesData, severitiesData, likelihoodsData] =
        await Promise.all([
          MetadataService.getRiskCreationCategories(
            organizationId,
            getAccessTokenSilently
          ),
          MetadataService.getRiskCreationSeverities(
            organizationId,
            getAccessTokenSilently
          ),
          MetadataService.getRiskCreationLikelihoods(
            organizationId,
            getAccessTokenSilently
          ),
        ]);

      setCategories(categoriesData);
      setSeverities(severitiesData);
      setLikelihoods(likelihoodsData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && organizationId) {
      fetchMetadata();
      // Reset form when opening
      setFormData({
        name: "",
        description: "",
        category_id: "",
        severity_id: "",
        likelihood_id: "",
      });
      setIsOrganizationLevel(forceOrganizationLevel);
    }
  }, [
    open,
    organizationId,
    businessUnitId,
    isOrganizationLevel,
    forceOrganizationLevel,
    getAccessTokenSilently,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLevelChange = (newIsOrganizationLevel) => {
    setIsOrganizationLevel(newIsOrganizationLevel);
    // Reset metadata selections when level changes
    setFormData((prev) => ({
      ...prev,
      category_id: "",
      severity_id: "",
      likelihood_id: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Risk name is required");
      return;
    }

    if (!formData.description.trim()) {
      setError("Risk description is required");
      return;
    }

    if (!organizationId) {
      setError("Organization ID is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const riskData = {
        ...formData,
        organization_id: organizationId,
        // Only include business_unit_id if creating business unit-level risk
        ...(isOrganizationLevel ? {} : { business_unit_id: businessUnitId }),
      };

      await RiskService.createRisk(riskData, getAccessTokenSilently);

      // Reset form
      setFormData({
        name: "",
        description: "",
        category_id: "",
        severity_id: "",
        likelihood_id: "",
      });
      setIsOrganizationLevel(forceOrganizationLevel);

      onRiskCreated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: "",
        description: "",
        category_id: "",
        severity_id: "",
        likelihood_id: "",
      });
      setIsOrganizationLevel(forceOrganizationLevel);
      setError(null);
      onClose();
    }
  };

  const getRiskLevelColor = () => {
    const severity = severities.find((s) => s.id === formData.severity_id);
    const likelihood = likelihoods.find((l) => l.id === formData.likelihood_id);

    if (!severity || !likelihood) return "text-foreground";

    const riskLevel = severity.value * likelihood.value;

    if (riskLevel >= 15) return "text-red-600";
    if (riskLevel >= 9) return "text-orange-600";
    if (riskLevel >= 5) return "text-blue-600";
    return "text-green-600";
  };

  const canCreateOrganizationLevelRisk =
    isOrgAdmin && organizationId && !forceOrganizationLevel;

  // Format metadata display - all metadata is now organization-level
  const formatMetadataOption = (item, type) => {
    if (type === "category") {
      return (
        <div className="flex items-center justify-between w-full">
          <span>
            {item.name}
            {item.description ? ` - ${item.description}` : ""}
          </span>
          <Badge variant="outline" className="ml-2 text-caption">
            Org
          </Badge>
        </div>
      );
    }

    if (type === "severity") {
      return (
        <div className="flex items-center justify-between w-full">
          <span>
            {item.name} (Level {item.value})
            {item.description ? ` - ${item.description}` : ""}
          </span>
          <Badge variant="outline" className="ml-2 text-caption">
            Org
          </Badge>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between w-full">
        <span>
          {item.description} (Level {item.value})
        </span>
        <Badge variant="outline" className="ml-2 text-caption">
          Org
        </Badge>
      </div>
    );
  };

  const hasNoMetadata =
    !loading &&
    (categories.length === 0 ||
      severities.length === 0 ||
      likelihoods.length === 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Risk</DialogTitle>
      </DialogHeader>

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      {hasNoMetadata ? (
        <Alert className="mt-4">
          No metadata (categories, severities, or likelihoods) has been set up
          for this organization. Please set up the required metadata first.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {canCreateOrganizationLevelRisk && (
            <FormItem>
              <div className="flex items-center space-x-2">
                <Switch
                  id="risk-level"
                  checked={isOrganizationLevel}
                  onCheckedChange={handleLevelChange}
                  disabled={forceOrganizationLevel}
                />
                <FormLabel htmlFor="risk-level" className="!mt-0">
                  Create as Organization-Level Risk
                </FormLabel>
              </div>
            </FormItem>
          )}

          <FormItem>
            <FormLabel>Risk Name *</FormLabel>
            <FormControl>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Enter a concise name for the risk..."
                disabled={submitting}
              />
            </FormControl>
            <FormDescription>
              Provide a clear, concise name for the risk
            </FormDescription>
          </FormItem>

          <FormItem>
            <FormLabel>Risk Description *</FormLabel>
            <FormControl>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe the risk in detail..."
                className="min-h-[100px]"
                disabled={submitting}
              />
            </FormControl>
            <FormDescription>
              Provide a detailed description of the risk
            </FormDescription>
          </FormItem>

          <FormItem>
            <FormLabel>Category</FormLabel>
            <Select
              value={formData.category_id}
              onValueChange={(value) => handleInputChange("category_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {formatMetadataOption(category, "category")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>

          <FormItem>
            <FormLabel>Severity</FormLabel>
            <Select
              value={formData.severity_id}
              onValueChange={(value) => handleInputChange("severity_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select severity" />
              </SelectTrigger>
              <SelectContent>
                {severities.map((severity) => (
                  <SelectItem key={severity.id} value={severity.id}>
                    {formatMetadataOption(severity, "severity")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>

          <FormItem>
            <FormLabel>Likelihood</FormLabel>
            <Select
              value={formData.likelihood_id}
              onValueChange={(value) =>
                handleInputChange("likelihood_id", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select likelihood" />
              </SelectTrigger>
              <SelectContent>
                {likelihoods.map((likelihood) => (
                  <SelectItem key={likelihood.id} value={likelihood.id}>
                    {formatMetadataOption(likelihood, "likelihood")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                submitting ||
                hasNoMetadata ||
                !formData.name.trim() ||
                !formData.description.trim()
              }
            >
              {submitting ? <Spinner className="mr-2" /> : null}
              Create Risk
            </Button>
          </DialogFooter>
        </form>
      )}
    </>
  );
};

export default CreateRiskForm;
